import {useTranslation} from 'react-i18next';
import React, {Fragment, useEffect, useState} from 'react';
import makeStyles from '@mui/styles/makeStyles';
import moment from 'moment';
import _ from 'lodash';
import {useSelector} from 'react-redux';
import {user} from '../../../store/reducers/user';
import Notes from '../../../util/notes';
import Formatter from '../../../util/formatter';
import API from '../../../app/api';
import clsx from 'clsx';
import AgyleSwitch from '../../../components/ui/switch/agyleSwitch';
import AgyleDatePicker from '../../../components/ui/fields/agyleDatePicker';
import AgyleBlockSwitch from '../../../components/ui/switch/agyleBlockSwitch';
import {
    addOrReplace,
    addOrReplaceChangeFlag,
    findPatientFlagTitle,
    isFlagDisabled,
    patientFlags,
} from '../mainScreen/util/patientFlagsUtil';
import {getConfig} from '../../../util/configUtil';
import MiniFormDialog from '../../../components/modal/miniFormDialog';

const useStyles = makeStyles((theme) => ({
    root: {
        maxHeight: 'calc(100vh - 400px)',
    },
    body: {
        padding: 5,
    },
    flagContainer: {
        width: '100%',
        background: theme.palette.inPatient.inputBackground,
        padding: 5,
        borderRadius: 8,
    },
    flagTitle: {
        display: 'block',
        width: '100%',
        fontWeight: 'bold',
        borderBottom: '1px solid #b7b7b7',
        padding: '1px 0px 1px 5px',
    },
    flagBody: {
        background: theme.palette.background.paper,
        paddingLeft: 10,
    },
    flagText: {
        float: 'right',
        padding: '7px 5px',
    },
    dataPicker: {
        padding: 10,
        width: '100%',
    },
    error: {
        color: '#f44336',
    },
    text: {
        width: '100%',
        marginTop: 5,
        marginLeft: 15,
        fontSize: 14,
        fontFamily: 'Inter',
        fontWeight: 'bold',
    },
    creatorText: {
        color: theme.palette.mode === 'light' ? '#494B4C' : '#ACAFB3',
        fontSize: 12,
        fontFamily: 'Inter',
        fontWeight: 500,
        minHeight: 19,
        marginLeft: 15,
    },
    divRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
    },
}));

const PatientFlags = (props) => {
    const {t} = useTranslation();
    const classes = useStyles();
    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);

    // Form State
    const [flags, setFlags] = useState([]);
    const [date111, setDate111] = useState(null);
    const [deceasedDate, setDeceasedDate] = useState(null);
    const [transport, setTransport] = useState(false);

    // Configs
    const deceasedDateEnabled = getConfig('modules.patient.dodDateUpdateEnabled', false);
    const emObDnDEnabled = getConfig('emObDnDEnabled', false);
    const defaultDateTimeFormat = getConfig('defaultDateTimeFormat');

    // Init data
    useEffect(() => {
        let initFlags = [];
        _.forEach(patientFlags, flag => {
            if (!isFlagDisabled(flag.name)) {
                if (!flag.enabled) return;
                let obj = {
                    name: flag.name,
                    checked: false,
                    by: null,
                    date: null,
                };

                if (flag.name === '111') {
                    obj = {
                        ...obj,
                        when111: moment().toISOString(),
                    };
                }
                initFlags.push(obj);
            }
        });

        if (!_.isEmpty(props.patient?.metadata?.patientFlags)) {
            props.patient.metadata.patientFlags.forEach(el => {
                if (!isFlagDisabled(el.name)) addOrReplace(initFlags, el);
            });
        }

        const flag111 = _.find(initFlags, {name: '111'});
        if (flag111) {
            const setDate = (date) => {
                return moment(date).isValid() ? moment(date).toISOString() : moment(date, defaultDateTimeFormat).toISOString();
            };

            setDate111(flag111.when111 ? setDate(flag111.when111) : null);
        }

        const flagDeceased = _.find(initFlags, {name: 'deceased'});
        if (flagDeceased && deceasedDateEnabled) {
            setDeceasedDate(flagDeceased.deceasedDate ? moment(flagDeceased.deceasedDate).toISOString() : null);
        }

        const flagTransport = _.find(initFlags, {name: 'transport'});
        if (flagTransport) setTransport(flagTransport.ready || false);

        setFlags(initFlags);
    }, []);

    const checkChangesFlags = () => {
        const currentFlags = _.cloneDeep(props.patient?.metadata?.patientFlags);
        let changes = [];

        _.forEach(currentFlags, element => {
            const findFlag = _.find(flags, {name: element.name});
            if (findFlag && findFlag.checked !== element.checked) {
                changes.push(findFlag);
            }
        });

        if (_.isEmpty(currentFlags)) {
            _.forEach(flags, element => {
                if (!_.isNil(element.date)) {
                    changes.push(element);
                }
            });
        }

        return changes;
    };

    const prepareNote = (date) => {
        const changeInFlags = checkChangesFlags();
        return Notes.getPatientFlagsNote(changeInFlags);
    };

    const nilByMouth = () => {
        let arr = props.patient?.flags ? _.cloneDeep(props.patient.flags) : [];
        const find = _.find(arr, {name: 'nbm'});
        const nbm = _.find(flags, {name: 'nbm'});

        if (nbm?.checked) {
            if (!find) {
                arr.unshift({name: 'nbm', value: 1});
            }
        } else {
            arr = _.filter(arr, el => {
                return el.name !== 'nbm';
            });
        }
        return {
            name: 'flags',
            value: arr,
        };
    };

    const submit = async () => {
        if (!_.isEqual(flags, props.patient?.metadata?.patientFlags)) {
            const dateOfForm = moment().toISOString();
            let data = [{
                name: 'metadata.patientFlags',
                value: flags,
                noteToAdd: prepareNote(dateOfForm),
            }];

            const checkDeceased = _.find(flags, {name: 'deceased'});

            // Change status for clinical conclusion
            if (checkDeceased?.checked) {
                const clinician = await findClinician();
                const clinicalConclusion = {
                    who: clinician,
                    login: userObject?.userName,
                    when: dateOfForm,
                    status: 'Deceased',
                };

                data.push({
                    name: 'clinicalConclusion',
                    value: clinicalConclusion,
                    noteToAdd: Notes.getClinicalConclusionNote(clinicalConclusion, null, deceasedDate),
                });
            }

            if (emObDnDEnabled) {
                const isEmObs = !_.isNil(props.patient?.metadata?.emObs?.timeStamp);
                const emObFlag = _.find(flags, {name: 'emob'});

                if (isEmObs && emObFlag?.checked === false) {
                    data.push({
                        name: 'metadata.emObs',
                        value: {timeStamp: null},
                    });
                } else if (!isEmObs && emObFlag?.checked === true) {
                    data.push({
                        name: 'metadata.emObs',
                        value: {timeStamp: moment().toISOString()},
                    });
                }
            }
            const nbm = nilByMouth();
            if (!_.isEqual(props.patient.flags, nbm?.value)) data.push(nilByMouth());
            props.onSave(data);
        }
        props.onClose();
    };

    const findClinician = async () => {
        const newFilter = 'deactivated ne true';
        const login = await API.getDictByURL(`/$dict/ClinicalConclusionClinicians?$filter=${encodeURIComponent(newFilter)}`)
            .then((dictData) => {
                if (dictData) {
                    for (let clinician of dictData.__data) {
                        if (clinician.userName === userObject?.userName) {
                            return clinician;
                        }
                    }
                }
            });

        return login || tester;
    };

    const setOption = (name, value, time) => {
        return {
            name: name,
            checked: value,
            by: tester,
            login: userObject?.userName,
            date: time,
        };
    };

    const handleOnlyOneOption = (name, value) => {
        const time = moment().toISOString();

        let fitToSit;
        let seeAndTreat;
        let fracture;
        let inCar;
        let res;
        let maj;
        let amb;
        let min;
        let mh;
        let bedas;
        let emob;

        // eslint-disable-next-line default-case
        switch (name) {
            case 'fitToSit' : {
                fitToSit = {
                    name: 'fitToSit',
                    checked: value,
                    by: tester,
                    login: userObject?.userName,
                    date: time,
                };
                seeAndTreat = {
                    name: 'seeAndTreat',
                    checked: false,
                    by: tester,
                    login: userObject?.userName,
                    date: time,
                };
                fracture = {
                    name: 'fracture',
                    checked: false,
                    by: tester,
                    login: userObject?.userName,
                    date: time,
                };
                break;
            }
            case 'seeAndTreat' : {
                fitToSit = {
                    name: 'fitToSit',
                    checked: false,
                    by: tester,
                    login: userObject?.userName,
                    date: time,
                };
                seeAndTreat = {
                    name: 'seeAndTreat',
                    checked: value,
                    by: tester,
                    login: userObject?.userName,
                    date: time,
                };
                fracture = {
                    name: 'fracture',
                    checked: false,
                    by: tester,
                    login: userObject?.userName,
                    date: time,
                };
                break;
            }
            case 'fracture' : {
                fitToSit = {
                    name: 'fitToSit',
                    checked: false,
                    by: tester,
                    login: userObject?.userName,
                    date: time,
                };
                seeAndTreat = {
                    name: 'seeAndTreat',
                    checked: false,
                    by: tester,
                    login: userObject?.userName,
                    date: time,
                };
                fracture = {
                    name: 'fracture',
                    checked: value,
                    by: tester,
                    login: userObject?.userName,
                    date: time,
                };
                break;
            }
            case 'inCar' : {
                inCar = setOption('inCar', value, time);
                res = setOption('res', false, time);
                maj = setOption('maj', false, time);
                amb = setOption('amb', false, time);
                min = setOption('min', false, time);
                mh = setOption('mh', false, time);
                bedas = setOption('bedas', false, time);
                emob = setOption('emob', false, time);
                break;
            }
            case 'res' : {
                inCar = setOption('inCar', false, time);
                res = setOption('res', value, time);
                maj = setOption('maj', false, time);
                amb = setOption('amb', false, time);
                min = setOption('min', false, time);
                mh = setOption('mh', false, time);
                bedas = setOption('bedas', false, time);
                emob = setOption('emob', false, time);
                break;
            }
            case 'maj' : {
                inCar = setOption('inCar', false, time);
                res = setOption('res', false, time);
                maj = setOption('maj', value, time);
                amb = setOption('amb', false, time);
                min = setOption('min', false, time);
                mh = setOption('mh', false, time);
                bedas = setOption('bedas', false, time);
                emob = setOption('emob', false, time);
                break;
            }
            case 'amb' : {
                inCar = setOption('inCar', false, time);
                res = setOption('res', false, time);
                maj = setOption('maj', false, time);
                amb = setOption('amb', value, time);
                min = setOption('min', false, time);
                mh = setOption('mh', false, time);
                bedas = setOption('bedas', false, time);
                emob = setOption('emob', false, time);
                break;
            }
            case 'min' : {
                inCar = setOption('inCar', false, time);
                res = setOption('res', false, time);
                maj = setOption('maj', false, time);
                amb = setOption('amb', false, time);
                min = setOption('min', value, time);
                mh = setOption('mh', false, time);
                bedas = setOption('bedas', false, time);
                emob = setOption('emob', false, time);
                break;
            }
            case 'mh': {
                inCar = setOption('inCar', false, time);
                res = setOption('res', false, time);
                maj = setOption('maj', false, time);
                amb = setOption('amb', false, time);
                min = setOption('min', false, time);
                mh = setOption('mh', value, time);
                bedas = setOption('bedas', false, time);
                emob = setOption('emob', false, time);
                break;
            }
            case 'bedas': {
                inCar = setOption('inCar', false, time);
                res = setOption('res', false, time);
                maj = setOption('maj', false, time);
                amb = setOption('amb', false, time);
                min = setOption('min', false, time);
                mh = setOption('mh', false, time);
                bedas = setOption('bedas', value, time);
                emob = setOption('emob', false, time);
                break;
            }
            case 'emob': {
                inCar = setOption('inCar', false, time);
                res = setOption('res', false, time);
                maj = setOption('maj', false, time);
                amb = setOption('amb', false, time);
                min = setOption('min', false, time);
                mh = setOption('mh', false, time);
                bedas = setOption('bedas', false, time);
                emob = setOption('emob', value, time);
                break;
            }
        }
        const clone = _.cloneDeep(flags);
        if (fitToSit) addOrReplace(clone, fitToSit);
        if (seeAndTreat) addOrReplace(clone, seeAndTreat);
        if (inCar) addOrReplaceChangeFlag(clone, inCar);
        if (res) addOrReplaceChangeFlag(clone, res);
        if (maj) addOrReplaceChangeFlag(clone, maj);
        if (amb) addOrReplaceChangeFlag(clone, amb);
        if (min) addOrReplaceChangeFlag(clone, min);
        if (mh) addOrReplaceChangeFlag(clone, mh);
        if (fracture) addOrReplaceChangeFlag(clone, fracture);
        if (bedas) addOrReplaceChangeFlag(clone, bedas);
        if (emob) addOrReplaceChangeFlag(clone, emob);

        setFlags(clone);
    };

    const handleChange = (event) => {
        const {name, checked} = event.target;
        const time = moment().toISOString();
        const clone = _.cloneDeep(flags);

        switch (name) {
            case 'fitToSit':
            case 'seeAndTreat':
            case 'res':
            case 'maj':
            case 'amb':
            case 'min':
            case 'inCar':
            case 'mh':
            case 'fracture':
            case 'bedas':
            case 'emob':
                handleOnlyOneOption(name, checked);
                break;
            case '111':
                if (!checked) {
                    setDate111(time);
                }
                const obj111 = {
                    name: name,
                    checked: checked,
                    when111: event.target.date111 ? event.target.date111 : moment(),
                    by: tester,
                    login: userObject?.userName,
                    date: time,
                };
                addOrReplace(clone, obj111);

                setFlags(clone);
                break;
            case 'transport':
                const flagObj = {
                    name: name,
                    checked: checked,
                    ready: event.target.transport,
                    by: tester,
                    login: userObject?.userName,
                    date: time,
                };
                addOrReplace(clone, flagObj);

                setFlags(clone);
                break;
            case 'deceased':
                let objDeceased = {
                    name: name,
                    checked: checked,
                    by: tester,
                    login: userObject?.userName,
                    date: time,
                };

                if (deceasedDateEnabled) {
                    if (checked && !deceasedDate) {
                        setDeceasedDate(time);
                    }
                    objDeceased = {
                        ...objDeceased,
                        deceasedDate: event.target.deceasedDate ? event.target.deceasedDate
                            : checked ? moment() : null,
                    };
                }

                addOrReplace(clone, objDeceased);

                setFlags(clone);
                break;
            default:
                const obj = {
                    name: name,
                    checked: checked,
                    by: tester,
                    login: userObject?.userName,
                    date: time,
                };
                addOrReplace(clone, obj);

                setFlags(clone);
                break;
        }
    };

    const handleChangeDate = (event, name) => {
        const date = moment(event).toISOString();
        if (name === '111') setDate111(date);
        if (name === 'deceased') setDeceasedDate(date);

        handleChange({
            target: {
                name: name,
                checked: true,
                [name === '111' ? 'date111' : 'deceasedDate']: date,
            },
        });
    };

    const handleChangeSwitch = (checked, name) => {
        setTransport(checked);

        handleChange({
            target: {
                name: name,
                checked: true,
                transport: checked,
            },
        });
    };

    // Check for old format date
    const checkDateText = (date) => {
        return moment(date).isValid() ? moment(date).format(defaultDateTimeFormat) : date;
    };

    const renderFlagCtx = (flag, index) => {

        return (
            <Fragment key={flag.name}>
                {index !== 0 && <div style={{margin: '4px 0'}}/>}

                <div className={clsx(classes.divRow, classes.flagContainer)}>
                    <div>
                        <div
                            className={clsx(classes.text)}
                        >
                            {t(findPatientFlagTitle(flag.name))}
                        </div>

                        <div className={classes.creatorText}>
                            {flag.by &&
                                <span>
                                    {`${flag.by} ${checkDateText(flag.date)}`}
                                </span>
                            }
                        </div>
                    </div>

                    <div style={{marginLeft: 'auto'}}>
                        {(flag.checked && flag.name === '111') &&
                            <AgyleDatePicker
                                label={t('Date and Time')}
                                value={date111}
                                onChange={event => handleChangeDate(event, flag.name)}
                                type="dateTime"
                                error={!date111}
                                noBorder={false}
                                withNewBackground
                            />
                        }

                        {(flag.checked && flag.name === 'deceased' && deceasedDateEnabled) &&
                            <AgyleDatePicker
                                label={t('Date and Time')}
                                value={deceasedDate}
                                onChange={event => handleChangeDate(event, flag.name)}
                                type="dateTime"
                                error={!deceasedDate}
                                noBorder={false}
                                withNewBackground
                            />
                        }

                        {(flag.checked && flag.name === 'transport') &&
                            <AgyleBlockSwitch
                                onChange={event => handleChangeSwitch(event, flag.name)}
                                value={transport}
                                customLabels={{yes: t('Ready'), no: t('Requested')}}
                            />
                        }
                    </div>

                    <div style={{marginLeft: 15}}>
                        <AgyleSwitch
                            onChange={handleChange}
                            value={flag.checked}
                            name={flag.name}
                        />
                    </div>
                </div>
            </Fragment>
        );
    };

    return (
        <MiniFormDialog
            title={t('Patient Flags')}
            open={props.open}
            onClose={props.onClose}
            onSave={submit}
            disableSubmit={props.disableSave}
        >
            <div className={classes.root}>
                {flags.map((element, index) => renderFlagCtx(element, index))}
            </div>
        </MiniFormDialog>
    );
};

export default PatientFlags;

